<template>
  <div class="d-flex flex-column flex-column-fluid">
    <!-- Toolbar -->
    <div id="kt_app_toolbar" class="app-toolbar py-3 py-lg-6">
      <div
        id="kt_app_toolbar_container"
        class="app-container container-fluid d-flex flex-stack"
      >
        <div
          class="page-title d-flex flex-column justify-content-center flex-wrap me-3"
        >
          <h1
            class="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0"
          >
            {{ $t("common.app_name") }}
          </h1>
          <ul
            class="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1"
          >
            <li class="breadcrumb-item text-muted">
              <a
                href="../../demo1/dist/index.html"
                class="text-muted text-hover-success"
                >{{ $t("common.home") }}</a
              >
            </li>
            <li class="breadcrumb-item">
              <span class="bullet bg-gray-400 w-5px h-2px"></span>
            </li>
            <li class="breadcrumb-item text-muted">
              {{ $t("common.settings") }}
            </li>
          </ul>
        </div>
        <div class="d-flex align-items-center gap-2 gap-lg-3"></div>
      </div>
    </div>
    <!-- Content -->
    <div id="kt_app_content" class="app-content flex-column-fluid">
      <div id="kt_app_content_container" class="app-container container-fluid">
        <div
          v-if="isLoading == true"
          style="
            font-weight: 800;
            border-radius: 10px;
            padding: 50px;
            font-size: large;
            text-align: center;
            background: #73737326;
          "
          class="loader text-success"
        >
          {{ $t("common.loading") }}
          <div class="spinner-border text-success" role="status">
            <span class="sr-only"></span>
          </div>
        </div>
        <div v-if="isLoggedIn == false && isLoading == false" class="col-12">
          <div class="card text-center p-5 not-login">
            <span class="icon"><i class="bi bi-person-badge"></i></span>
            <h3 class="mb-4">{{ $t("common.please_login_to_continue") }}</h3>
            <span>
              <a href="login" class="btn btn-success">
                {{ $t("common.login") }} <i class="fa fa-sign-in"></i>
              </a>
            </span>
          </div>
        </div>
        <div
          v-if="isLoggedIn == true && isLoading == false"
          class="d-flex flex-column flex-lg-row"
        >
          <!-- Sidebar -->
          <div class="flex-column flex-lg-row-auto w-lg-250px w-xl-350px mb-10">
            <div class="card mb-5 mb-xl-8">
              <div class="card-body">
                <!--begin::Summary-->
                <!--begin::User Info-->
                <div class="d-flex flex-center flex-column py-5">
                  <!--begin::Avatar-->
                  <div class="symbol symbol-100px symbol-circle mb-7 avatar-wrapper">
                    <img 
                      :src="userAvatarUrl" 
                      @error="handleImageError"
                      alt="image" 
                    />
                    <button 
                      class="avatar-edit-btn" 
                      @click="triggerAvatarUpload"
                      :title="$t('settings.change_photo')"
                    >
                      <i class="fas fa-camera"></i>
                    </button>
                    <input
                      type="file"
                      ref="avatarInput"
                      @change="handleAvatarChange"
                      accept=".jpg,.jpeg,.png,.bmp,.tiff"
                      class="hidden-input"
                    />
                  </div>
                  <!--end::Avatar-->
                  <!--begin::Name-->
                  <a
                    href="#"
                    class="fs-3 text-gray-800 text-hover-primary fw-bold mb-3 d-flex align-items-center gap-2"
                  >
                    {{ student.fname }} {{ student.lname }}
                    <i 
                      v-if="student.email_verified_at" 
                      class="fas fa-check-circle text-success fs-6" 
                      :title="$t('settings.verified')"
                    ></i>
                  </a>
                  <!--end::Name-->
                  <!--begin::Position-->
                  <div class="mb-9">
                    <!--begin::Badge-->
                    <div class="badge badge-lg badge-light-success d-inline">
                      {{ $t("settings.student_badge") }}
                    </div>
                    <!--begin::Badge-->
                  </div>
                  <!--end::Position-->
                  <!--begin::Info-->
                  <!--begin::Info heading-->
                  <div class="fw-bold mb-3">
                    {{ $t("settings.watermark") }}: {{ student.watermark_id }}
                    <i
                      class="fas fa-exclamation-circle ms-2 fs-7"
                      data-bs-toggle="popover"
                      data-bs-trigger="hover"
                      data-bs-html="true"
                      :data-bs-content="$t('settings.watermark_tooltip')"
                      data-kt-initialized="1"
                    ></i>
                  </div>
                  <!--end::Info-->
                </div>
                <!--end::User Info-->
                <!--end::Summary-->
                <!--begin::Settings Options-->
                <div class="settings-options mt-4 pt-4 border-top">
                  <!--begin::Preferences Group-->
                  <div class="options-group">
                    <div class="option-item" data-bs-toggle="modal" data-bs-target="#languageModal">
                      <div class="option-icon">
                        <i class="fas fa-globe"></i>
                      </div>
                      <div class="option-content">
                        <span class="option-title">{{ $t("settings.language") }}</span>
                        <span class="option-value">{{ currentLanguageLabel }}</span>
                      </div>
                      <i class="fas fa-chevron-left option-arrow"></i>
                    </div>
                    <div class="option-item" data-bs-toggle="modal" data-bs-target="#themeModal">
                      <div class="option-icon">
                        <i class="fas fa-palette"></i>
                      </div>
                      <div class="option-content">
                        <span class="option-title">{{ $t("settings.theme") }}</span>
                        <span class="option-value">{{ currentThemeLabel }}</span>
                      </div>
                      <i class="fas fa-chevron-left option-arrow"></i>
                    </div>
                  </div>
                  <!--end::Preferences Group-->

                  <!--begin::Device Management Group (Master Only)-->
                  <div class="options-group" v-if="isMaster">
                    <div class="option-item" @click="showDeleteSessionsConfirm">
                      <div class="option-icon text-danger">
                        <i class="fas fa-trash-alt"></i>
                      </div>
                      <div class="option-content">
                        <span class="option-title">{{ $t("settings.delete_all_sessions") }}</span>
                      </div>
                      <i class="fas fa-chevron-left option-arrow"></i>
                    </div>
                    <div class="option-item" data-bs-toggle="modal" data-bs-target="#deviceQrModal" @click="generateDeviceQrCode">
                      <div class="option-icon">
                        <i class="fas fa-qrcode"></i>
                      </div>
                      <div class="option-content">
                        <span class="option-title">{{ $t("settings.device_qr") }}</span>
                      </div>
                      <i class="fas fa-chevron-left option-arrow"></i>
                    </div>
                  </div>
                  <!--end::Device Management Group-->

                  <!--begin::Chat Group-->
                  <div class="options-group">
                    <div class="option-item" data-bs-toggle="modal" data-bs-target="#chatSettingsModal" @click="fetchChatSettings">
                      <div class="option-icon">
                        <i class="fas fa-comments"></i>
                      </div>
                      <div class="option-content">
                        <span class="option-title">{{ $t("settings.chat_settings") }}</span>
                      </div>
                      <i class="fas fa-chevron-left option-arrow"></i>
                    </div>
                    <div class="option-item" data-bs-toggle="modal" data-bs-target="#chatQrModal" @click="fetchChatShareableId">
                      <div class="option-icon">
                        <i class="fas fa-qrcode"></i>
                      </div>
                      <div class="option-content">
                        <span class="option-title">{{ $t("settings.chat_qr") }}</span>
                      </div>
                      <i class="fas fa-chevron-left option-arrow"></i>
                    </div>
                  </div>
                  <!--end::Chat Group-->

                  <!--begin::Support Group-->
                  <div class="options-group">
                    <div class="option-item" data-bs-toggle="modal" data-bs-target="#faqModal" @click="fetchFaqItems">
                      <div class="option-icon">
                        <i class="fas fa-question-circle"></i>
                      </div>
                      <div class="option-content">
                        <span class="option-title">{{ $t("settings.faq") }}</span>
                      </div>
                      <i class="fas fa-chevron-left option-arrow"></i>
                    </div>
                    <div class="option-item" data-bs-toggle="modal" data-bs-target="#contactModal">
                      <div class="option-icon">
                        <i class="fas fa-envelope"></i>
                      </div>
                      <div class="option-content">
                        <span class="option-title">{{ $t("settings.contact_us") }}</span>
                      </div>
                      <i class="fas fa-chevron-left option-arrow"></i>
                    </div>
                  </div>
                  <!--end::Support Group-->

                  <!--begin::App Info Group-->
                  <div class="options-group">
                    <div class="option-item" data-bs-toggle="modal" data-bs-target="#aboutModal" @click="fetchAboutContent">
                      <div class="option-icon">
                        <i class="fas fa-info-circle"></i>
                      </div>
                      <div class="option-content">
                        <span class="option-title">{{ $t("settings.about_app") }}</span>
                      </div>
                      <i class="fas fa-chevron-left option-arrow"></i>
                    </div>
                    <div class="option-item" @click="shareApp">
                      <div class="option-icon">
                        <i class="fas fa-share-alt"></i>
                      </div>
                      <div class="option-content">
                        <span class="option-title">{{ $t("settings.share_app") }}</span>
                      </div>
                      <i class="fas fa-chevron-left option-arrow"></i>
                    </div>
                  </div>
                  <!--end::App Info Group-->
                </div>
                <!--end::Settings Options-->

                <!--begin::Logout Button-->
                <div class="d-flex flex-column gap-3 mt-4 pt-4 border-top">
                  <button 
                    class="btn btn-light-danger w-100"
                    @click="handleLogout"
                  >
                    <i class="fas fa-sign-out-alt me-2"></i>
                    {{ $t("common.logout") }}
                  </button>
                </div>
                <!--end::Logout Button-->
                <!--begin::App Version-->
                <div class="text-center text-muted mt-4 pt-3 border-top">
                  <small>{{ $t("settings.version") }} {{ appVersion }}</small>
                </div>
                <!--end::App Version-->
              </div>
            </div>
          </div>
          <!-- Content -->
          <div class="flex-lg-row-fluid ms-lg-15">
            <!-- Tabs -->
            <ul
              class="nav nav-custom nav-tabs nav-line-tabs nav-line-tabs-2x border-0 fs-4 fw-semibold mb-8"
            >
              <li class="nav-item">
                <a
                  class="nav-link text-active-success pb-4 active"
                  data-bs-toggle="tab"
                  href="#kt_user_view_overview_tab"
                >
                  {{ $t("settings.tab_profile") }}
                </a>
              </li>
              <li class="nav-item" v-if="isMaster">
                <a class="nav-link text-active-success pb-4" data-bs-toggle="tab" href="#kt_user_view_devices_tab">
                  {{ $t('settings.tab_devices') }}
                </a>
              </li>
              <!-- Add more tabs if needed -->
            </ul>
            <!-- Tab content -->
            <div class="tab-content">
              <div
                class="tab-pane fade show active"
                id="kt_user_view_overview_tab"
                role="tabpanel"
              >
                <!-- Profile Info Card -->
                <div class="card pt-4 mb-6 mb-xl-9">
                  <div class="card-header border-0">
                    <div class="card-title">
                      <h3 class="fw-bold m-0">{{ $t("settings.profile_info") }}</h3>
                    </div>
                    <div class="card-toolbar">
                      <button 
                        v-if="!isEditing" 
                        class="btn btn-sm btn-success"
                        @click="startEditing"
                      >
                        <i class="fas fa-edit me-2"></i>
                        {{ $t("settings.edit_profile") }}
                      </button>
                      <div v-else class="d-flex gap-2">
                        <button 
                          class="btn btn-sm btn-light"
                          @click="cancelEditing"
                          :disabled="saving"
                        >
                          {{ $t("common.cancel") }}
                        </button>
                        <button 
                          class="btn btn-sm btn-success"
                          @click="saveProfile"
                          :disabled="saving"
                        >
                          <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
                          {{ $t("common.save") }}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class="card-body p-9">
                    <!-- View Mode -->
                    <div v-if="!isEditing" class="profile-view">
                      <!-- Personal Info Section -->
                      <div class="info-section">
                        <h6 class="section-title">
                          <i class="fas fa-user-circle"></i>
                          {{ $t("settings.personal_info") }}
                        </h6>
                        <div class="info-grid">
                          <div class="info-item">
                            <span class="info-label">{{ $t("settings.name") }}</span>
                            <span class="info-value">{{ student.fname }} {{ student.lname }}</span>
                          </div>
                          <div class="info-item">
                            <span class="info-label">{{ $t("settings.dob") }}</span>
                            <span class="info-value">{{ student.dob || $t("settings.not_set") }}</span>
                          </div>
                          <div class="info-item">
                            <span class="info-label">{{ $t("settings.phone") }}</span>
                            <span class="info-value">{{ student.mobile }}</span>
                          </div>
                          <div class="info-item full-width">
                            <span class="info-label">{{ $t("settings.email") }}</span>
                            <div class="info-value email-value">
                              <span>{{ student.email || '-' }}</span>
                              <span v-if="student.emailVerifiedAt" class="verified-badge">
                                <i class="fas fa-check-circle"></i>
                                {{ $t("settings.verified") }}
                              </span>
                              <span v-else class="unverified-badge">
                                <i class="fas fa-exclamation-circle"></i>
                                {{ $t("settings.not_verified") }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Academic Info Section -->
                      <div class="info-section">
                        <h6 class="section-title">
                          <i class="fas fa-graduation-cap"></i>
                          {{ $t("settings.academic_info") }}
                        </h6>
                        <div class="info-grid">
                          <div class="info-item">
                            <span class="info-label">{{ $t("settings.level") }}</span>
                            <span class="info-value">{{ profileInfo.level || '-' }}</span>
                          </div>
                          <div class="info-item">
                            <span class="info-label">{{ $t("settings.education") }}</span>
                            <span class="info-value">{{ profileInfo.education || '-' }}</span>
                          </div>
                          <div class="info-item">
                            <span class="info-label">{{ $t("settings.year") }}</span>
                            <span class="info-value">{{ profileInfo.year || '-' }}</span>
                          </div>
                        </div>
                      </div>

                    </div>

                    <!-- Edit Mode -->
                    <form v-else @submit.prevent="saveProfile" dir="rtl" class="edit-form">
                      <!-- Personal Info Section -->
                      <div class="form-section">
                        <h6 class="form-section-title">
                          <i class="fas fa-user-circle"></i>
                          {{ $t("settings.personal_info") }}
                        </h6>
                        <div class="row g-4">
                          <!-- First Name -->
                          <div class="col-md-6">
                            <label class="form-label fw-semibold">
                              <i class="fas fa-user text-success me-2"></i>
                              {{ $t("settings.first_name") }}
                            </label>
                            <input 
                              type="text" 
                              class="form-control" 
                              v-model="editForm.name"
                              :placeholder="$t('settings.enter_first_name')"
                            />
                            <small v-if="editErrors.name" class="text-danger">{{ editErrors.name }}</small>
                          </div>
                          <!-- Last Name -->
                          <div class="col-md-6">
                            <label class="form-label fw-semibold">
                              <i class="fas fa-user text-success me-2"></i>
                              {{ $t("settings.last_name") }}
                            </label>
                            <input 
                              type="text" 
                              class="form-control" 
                              v-model="editForm.lname"
                              :placeholder="$t('settings.enter_last_name')"
                            />
                            <small v-if="editErrors.lname" class="text-danger">{{ editErrors.lname }}</small>
                          </div>
                          <!-- Date of Birth -->
                          <div class="col-md-6">
                            <label class="form-label fw-semibold">
                              <i class="fas fa-calendar-alt text-success me-2"></i>
                              {{ $t("settings.dob") }}
                            </label>
                            <input 
                              type="date" 
                              class="form-control" 
                              v-model="editForm.dob"
                            />
                          </div>
                          <!-- Email -->
                          <div class="col-md-6">
                            <label class="form-label fw-semibold">
                              <i class="fas fa-envelope text-success me-2"></i>
                              {{ $t("settings.email") }}
                            </label>
                            <input 
                              type="email" 
                              class="form-control" 
                              v-model="editForm.email"
                              :placeholder="$t('settings.enter_email')"
                            />
                            <small v-if="editErrors.email" class="text-danger">{{ editErrors.email }}</small>
                          </div>
                        </div>
                      </div>

                      <!-- Academic Info Section -->
                      <div class="form-section">
                        <h6 class="form-section-title">
                          <i class="fas fa-graduation-cap"></i>
                          {{ $t("settings.academic_info") }}
                        </h6>
                        <div class="row g-4">
                          <!-- Level -->
                          <div class="col-md-4">
                            <label class="form-label fw-semibold">{{ $t("settings.level") }}</label>
                            <select 
                              class="form-control" 
                              v-model="editForm.level_id"
                              @change="onLevelChange"
                            >
                              <option value="" disabled>{{ $t("auth.choose_level") }}</option>
                              <option v-for="level in levels" :key="level.id" :value="level.id">
                                {{ level.name }}
                              </option>
                            </select>
                          </div>
                          <!-- Education -->
                          <div class="col-md-4">
                            <label class="form-label fw-semibold">{{ $t("settings.education") }}</label>
                            <select 
                              class="form-control" 
                              v-model="editForm.education_id"
                              :disabled="!editForm.level_id"
                            >
                              <option v-if="!filteredEducations.length" :value="null">...</option>
                              <option v-else value="" disabled>{{ $t("auth.choose_education") }}</option>
                              <option v-for="education in filteredEducations" :key="education.id" :value="education.id">
                                {{ education.name }}
                              </option>
                            </select>
                          </div>
                          <!-- Year -->
                          <div class="col-md-4">
                            <label class="form-label fw-semibold">{{ $t("settings.year") }}</label>
                            <select class="form-control" v-model="editForm.year_id">
                              <option value="" disabled>{{ $t("auth.choose_year") }}</option>
                              <option v-for="year in years" :key="year.id" :value="year.id">
                                {{ year.name }}
                              </option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <!-- Password Section with Toggle -->
                      <div class="form-section password-section">
                        <div class="password-toggle-header">
                          <h6 class="form-section-title mb-0">
                            <i class="fas fa-lock"></i>
                            {{ $t("settings.change_password") }}
                          </h6>
                          <label class="switch-container">
                            <input 
                              type="checkbox" 
                              v-model="showPasswordFields"
                              class="switch-input"
                            />
                            <span class="switch-slider"></span>
                          </label>
                        </div>
                        
                        <transition name="slide-fade">
                          <div v-if="showPasswordFields" class="password-fields">
                            <div class="row g-4 mt-3">
                              <!-- Current Password -->
                              <div class="col-md-6">
                                <label class="form-label fw-semibold">
                                  <i class="fas fa-key text-warning me-2"></i>
                                  {{ $t("settings.current_password") }}
                                </label>
                                <div class="password-input-wrapper">
                                  <input 
                                    :type="showCurrentPassword ? 'text' : 'password'" 
                                    class="form-control" 
                                    v-model="editForm.current_password"
                                    :placeholder="$t('settings.enter_current_password')"
                                  />
                                  <button 
                                    type="button" 
                                    class="password-toggle-btn"
                                    @click="showCurrentPassword = !showCurrentPassword"
                                  >
                                    <i :class="showCurrentPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                                  </button>
                                </div>
                                <small v-if="editErrors.current_password" class="text-danger">{{ editErrors.current_password }}</small>
                              </div>
                              <!-- New Password -->
                              <div class="col-md-6">
                                <label class="form-label fw-semibold">
                                  <i class="fas fa-lock text-success me-2"></i>
                                  {{ $t("settings.new_password") }}
                                </label>
                                <div class="password-input-wrapper">
                                  <input 
                                    :type="showNewPassword ? 'text' : 'password'" 
                                    class="form-control" 
                                    v-model="editForm.password"
                                    :placeholder="$t('settings.enter_new_password')"
                                  />
                                  <button 
                                    type="button" 
                                    class="password-toggle-btn"
                                    @click="showNewPassword = !showNewPassword"
                                  >
                                    <i :class="showNewPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                                  </button>
                                </div>
                                <small v-if="editErrors.password" class="text-danger">{{ editErrors.password }}</small>
                              </div>
                            </div>
                          </div>
                        </transition>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
              <div
                class="tab-pane fade"
                id="kt_user_view_devices_tab"
                role="tabpanel"
                dir="rtl"
              >
                <div class="devices-container">
                  <!-- Header مع زر الإضافة -->
                  <div class="devices-header">
                    <div class="header-content">
                      <div class="header-icon">
                        <i class="fa fa-mobile-alt"></i>
                      </div>
                      <div class="header-text">
                        <h4>{{ $t("settings.tab_devices") }}</h4>
                        <p>{{ $t("settings.manage_devices") }}</p>
                      </div>
                    </div>
                    <button
                      class="btn-add-device"
                      data-bs-toggle="modal"
                      data-bs-target="#addDeviceModal"
                    >
                      <i class="fa fa-plus-circle"></i>
                      <span>{{ $t("settings.add_new_device") }}</span>
                    </button>
                  </div>

                  <!-- قائمة الأجهزة -->
                  <div
                    class="devices-grid"
                    v-if="devices && devices.length > 0"
                  >
                    <div
                      v-for="device in devices"
                      :key="device.device_id"
                      class="device-card-new"
                    >
                      <div class="device-card-header">
                        <div class="device-icon">
                          <i
                            :class="
                              device.platform === 'Android'
                                ? 'fab fa-android'
                                : device.platform === 'iOS'
                                ? 'fab fa-apple'
                                : 'fa fa-desktop'
                            "
                          ></i>
                        </div>
                        <div
                          class="device-status"
                          :class="device.active === '1' ? 'active' : 'inactive'"
                        >
                          <span class="status-dot"></span>
                          {{
                            device.active === "1"
                              ? $t("common.active")
                              : $t("common.inactive")
                          }}
                        </div>
                      </div>
                      <div class="device-card-body">
                        <h5 class="device-name">{{ device.name }}</h5>
                        <div class="device-info">
                          <div class="info-item">
                            <i class="fa fa-microchip"></i>
                            <span>{{ device.platform }}</span>
                          </div>
                          <div class="info-item">
                            <i class="fa fa-map-marker-alt"></i>
                            <span>{{ device.location }}</span>
                          </div>
                          <div class="info-item">
                            <i class="fa fa-calendar-alt"></i>
                            <span>{{ device.created_at }}</span>
                          </div>
                        </div>
                      </div>
                      <div class="device-card-footer">
                        <div
                          class="qr-code-mini"
                          v-if="qrCodeUrls[device.device_id]"
                        >
                          <img
                            :src="qrCodeUrls[device.device_id]"
                            alt="QR Code"
                          />
                        </div>
                        <button
                          class="btn-delete-device"
                          @click="handleDelete(device.id)"
                          v-if="isMaster"
                        >
                          <i class="fa fa-trash-alt"></i>
                          <span>{{ $t("common.delete") }}</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- حالة التحميل -->
                  <div v-if="loading" class="devices-loading">
                    <div class="loading-spinner">
                      <div
                        class="spinner-border text-success"
                        role="status"
                      ></div>
                    </div>
                    <p>{{ $t("common.loading") }}</p>
                  </div>

                  <!-- لا توجد أجهزة -->
                  <div
                    v-if="!loading && (!devices || devices.length === 0)"
                    class="devices-empty"
                  >
                    <div class="empty-icon">
                      <i class="fa fa-mobile-alt"></i>
                    </div>
                    <h5>{{ $t("settings.no_devices") }}</h5>
                    <p>{{ $t("settings.add_device_to_start") }}</p>
                    <button
                      class="btn-add-first"
                      data-bs-toggle="modal"
                      data-bs-target="#addDeviceModal"
                    >
                      <i class="fa fa-plus"></i>
                      {{ $t("settings.add_device_now") }}
                    </button>
                  </div>
                </div>
              </div>
              <!-- Add more tab panes as per your requirement -->
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <addDevice />

  <!-- Language Modal -->
  <div class="modal fade" id="languageModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-sm">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ $t("settings.language") }}</h5>
          <button type="button" class="btn btn-light btn-sm" data-bs-dismiss="modal"><i class="fa fa-close"></i></button>
        </div>
        <div class="modal-body">
          <div class="language-options">
            <div class="language-option" :class="{ active: currentLanguage === 'ar' }" @click="changeLanguage('ar')">
              <span class="lang-name">العربية</span>
              <i v-if="currentLanguage === 'ar'" class="fas fa-check text-success"></i>
            </div>
            <div class="language-option" :class="{ active: currentLanguage === 'en' }" @click="changeLanguage('en')">
              <span class="lang-name">English</span>
              <i v-if="currentLanguage === 'en'" class="fas fa-check text-success"></i>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Theme Modal -->
  <div class="modal fade" id="themeModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-sm">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ $t("settings.theme") }}</h5>
          <button type="button" class="btn btn-light btn-sm" data-bs-dismiss="modal"><i class="fa fa-close"></i></button>
        </div>
        <div class="modal-body">
          <div class="theme-options">
            <div class="theme-option" :class="{ active: currentTheme === 'light' }" @click="changeTheme('light')">
              <i class="fas fa-sun"></i>
              <span>{{ $t("settings.theme_light") }}</span>
              <i v-if="currentTheme === 'light'" class="fas fa-check text-success ms-auto"></i>
            </div>
            <div class="theme-option" :class="{ active: currentTheme === 'dark' }" @click="changeTheme('dark')">
              <i class="fas fa-moon"></i>
              <span>{{ $t("settings.theme_dark") }}</span>
              <i v-if="currentTheme === 'dark'" class="fas fa-check text-success ms-auto"></i>
            </div>
            <div class="theme-option" :class="{ active: currentTheme === 'system' }" @click="changeTheme('system')">
              <i class="fas fa-desktop"></i>
              <span>{{ $t("settings.theme_system") }}</span>
              <i v-if="currentTheme === 'system'" class="fas fa-check text-success ms-auto"></i>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Device QR Modal -->
  <div class="modal fade" id="deviceQrModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ $t("settings.device_qr") }}</h5>
          <button type="button" class="btn btn-light btn-sm" data-bs-dismiss="modal"><i class="fa fa-close"></i></button>
        </div>
        <div class="modal-body text-center">
          <div class="qr-container-modal">
            <img v-if="deviceQrCodeUrl" :src="deviceQrCodeUrl" alt="Device QR Code" class="device-qr-image" />
            <div v-else class="qr-loading">
              <div class="spinner-border text-success" role="status"></div>
            </div>
          </div>
          <p class="text-muted mt-3 small">{{ $t("settings.device_qr_hint") }}</p>
        </div>
      </div>
    </div>
  </div>

  <!-- About Modal -->
  <div class="modal fade" id="aboutModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ $t("settings.about_app") }}</h5>
          <button type="button" class="btn btn-light btn-sm" data-bs-dismiss="modal"><i class="fa fa-close"></i></button>
        </div>
        <div class="modal-body">
          <div v-if="aboutLoading" class="text-center py-4">
            <div class="spinner-border text-success" role="status"></div>
          </div>
          <div v-else-if="aboutContent" class="about-content">
            <p>{{ aboutContent }}</p>
          </div>
          <div v-else class="about-content">
            <p>{{ $t("settings.about_default_text") }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- FAQ Modal -->
  <div class="modal fade" id="faqModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ $t("settings.faq") }}</h5>
          <button type="button" class="btn btn-light btn-sm" data-bs-dismiss="modal"><i class="fa fa-close"></i></button>
        </div>
        <div class="modal-body">
          <div v-if="faqLoading" class="text-center py-4">
            <div class="spinner-border text-success" role="status"></div>
          </div>
          <div v-else-if="faqItems.length > 0" class="faq-list">
            <div 
              v-for="(item, index) in faqItems" 
              :key="item.id || index" 
              class="faq-item"
            >
              <div 
                class="faq-question" 
                @click="toggleFaq(index)"
                :class="{ active: expandedFaq === index }"
              >
                <span>{{ item.title }}</span>
                <i :class="expandedFaq === index ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
              </div>
              <transition name="slide-fade">
                <div v-if="expandedFaq === index" class="faq-answer" v-html="item.details"></div>
              </transition>
            </div>
          </div>
          <div v-else class="text-center py-4 text-muted">
            {{ $t("settings.no_faq") }}
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Contact Us Modal -->
  <div class="modal fade" id="contactModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ $t("settings.contact_us") }}</h5>
          <button type="button" class="btn btn-light btn-sm" data-bs-dismiss="modal"><i class="fa fa-close"></i></button>
        </div>
        <div class="modal-body">
          <!-- Contact Info Cards -->
          <div class="contact-cards">
            <div class="contact-card">
              <div class="contact-card-icon">
                <i class="fas fa-map-marker-alt"></i>
              </div>
              <div class="contact-card-content">
                <h6>{{ $t("settings.address") }}</h6>
                <p>Hai Alandauls, Tripoli, Libya</p>
              </div>
            </div>
            <div class="contact-card" @click="copyEmail">
              <div class="contact-card-icon">
                <i class="fas fa-envelope"></i>
              </div>
              <div class="contact-card-content">
                <h6>{{ $t("settings.email") }}</h6>
                <p>info@academia.ly</p>
              </div>
              <i class="fas fa-copy contact-card-action"></i>
            </div>
            <div class="contact-card" @click="callPhone">
              <div class="contact-card-icon">
                <i class="fas fa-phone"></i>
              </div>
              <div class="contact-card-content">
                <h6>{{ $t("settings.phone") }}</h6>
                <p dir="ltr">+218 92 293 87 90</p>
              </div>
              <i class="fas fa-phone contact-card-action"></i>
            </div>
          </div>

          <!-- Contact Form -->
          <div class="contact-form-section mt-4">
            <h6 class="form-section-title mb-3">
              <i class="fas fa-paper-plane me-2"></i>
              {{ $t("settings.send_message") }}
            </h6>
            <form @submit.prevent="submitContactForm">
              <div class="row g-3">
                <div class="col-md-6">
                  <label class="form-label">{{ $t("settings.your_name") }} *</label>
                  <input 
                    type="text" 
                    class="form-control" 
                    v-model="contactForm.fname"
                    :placeholder="$t('settings.enter_your_name')"
                    required
                  />
                </div>
                <div class="col-md-6">
                  <label class="form-label">{{ $t("settings.phone") }} *</label>
                  <input 
                    type="tel" 
                    class="form-control" 
                    v-model="contactForm.mobile"
                    :placeholder="$t('settings.enter_phone')"
                    required
                  />
                </div>
                <div class="col-12">
                  <label class="form-label">{{ $t("settings.email") }} ({{ $t("common.optional") }})</label>
                  <input 
                    type="email" 
                    class="form-control" 
                    v-model="contactForm.email"
                    :placeholder="$t('settings.enter_email')"
                  />
                </div>
                <div class="col-12">
                  <label class="form-label">{{ $t("settings.your_message") }} *</label>
                  <textarea 
                    class="form-control" 
                    rows="4"
                    v-model="contactForm.message"
                    :placeholder="$t('settings.enter_message')"
                    required
                  ></textarea>
                </div>
                <div class="col-12">
                  <button 
                    type="submit" 
                    class="btn btn-success w-100"
                    :disabled="contactSubmitting"
                  >
                    <span v-if="contactSubmitting" class="spinner-border spinner-border-sm me-2"></span>
                    {{ $t("settings.send_message") }}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Chat Settings Modal -->
  <div class="modal fade" id="chatSettingsModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ $t("settings.chat_settings") }}</h5>
          <button type="button" class="btn btn-light btn-sm" data-bs-dismiss="modal"><i class="fa fa-close"></i></button>
        </div>
        <div class="modal-body">
          <div v-if="chatSettingsLoading" class="text-center py-4">
            <div class="spinner-border text-success" role="status"></div>
          </div>
          <div v-else class="chat-settings-form">
            <div class="setting-item">
              <div class="setting-info">
                <span class="setting-title">{{ $t("settings.allow_message_requests") }}</span>
                <span class="setting-desc">{{ $t("settings.allow_message_requests_desc") }}</span>
              </div>
              <label class="switch-container">
                <input 
                  type="checkbox" 
                  v-model="chatSettings.allow_message_requests"
                  class="switch-input"
                />
                <span class="switch-slider"></span>
              </label>
            </div>
            <div class="setting-item">
              <div class="setting-info">
                <span class="setting-title">{{ $t("settings.message_notifications") }}</span>
                <span class="setting-desc">{{ $t("settings.message_notifications_desc") }}</span>
              </div>
              <label class="switch-container">
                <input 
                  type="checkbox" 
                  v-model="chatSettings.message_notification"
                  class="switch-input"
                />
                <span class="switch-slider"></span>
              </label>
            </div>
            <button 
              class="btn btn-success w-100 mt-4"
              @click="saveChatSettings"
              :disabled="chatSettingsSaving"
            >
              <span v-if="chatSettingsSaving" class="spinner-border spinner-border-sm me-2"></span>
              {{ $t("common.save") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Chat QR Modal -->
  <div class="modal fade" id="chatQrModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ $t("settings.chat_qr") }}</h5>
          <button type="button" class="btn btn-light btn-sm" data-bs-dismiss="modal"><i class="fa fa-close"></i></button>
        </div>
        <div class="modal-body">
          <div v-if="chatQrLoading" class="text-center py-5">
            <div class="spinner-border text-success" role="status"></div>
          </div>
          <div v-else-if="chatShareableId" class="chat-qr-content">
            <!-- User Info -->
            <div v-if="chatUser" class="chat-user-info">
              <img :src="chatUser.image" :alt="chatUser.name" class="chat-user-avatar" @error="handleChatAvatarError" />
              <h6 class="chat-user-name">{{ chatUser.name }}</h6>
            </div>
            <!-- QR Code -->
            <div class="qr-container-modal">
              <img v-if="chatQrCodeUrl" :src="chatQrCodeUrl" alt="Chat QR Code" class="device-qr-image" />
            </div>
            <!-- Shareable ID with Copy -->
            <div class="shareable-id-box mt-4">
              <span class="shareable-id-label">{{ $t("settings.shareable_id") }}</span>
              <div class="shareable-id-value" @click="copyChatId">
                <code>{{ chatShareableId }}</code>
                <i class="fas fa-copy"></i>
              </div>
            </div>
            <p class="text-muted mt-3 small">{{ $t("settings.chat_qr_hint") }}</p>
          </div>
          <div v-else class="text-center py-4 text-muted">
            {{ $t("settings.chat_qr_error") }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiUrl, deviceId, userImagePath } from "@/utils/api"; // Adjust the import path according to your project structure
import QRCode from "qrcode";
import addDevice from "./addDevice.vue";
import Swal from "sweetalert2";
import i18n, { switchLocale } from "@/plugins/i18n";

export default {
  name: "Settings",
  components: {
    addDevice,
  },
  data() {
    return {
      devices: [],
      isLoggedIn: false,
      isLoading: true,
      isMaster: true,
      token: localStorage.getItem("access_token"),
      baseUrl: process.env.VUE_APP_BASE_URL,
      student: {},
      profileInfo: {
        level: null,
        education: null,
        year: null,
      },
      walletBalance: 0,
      loading: true,
      qrCodeUrls: {},
      // Edit mode
      isEditing: false,
      saving: false,
      editForm: {
        name: "",
        lname: "",
        email: "",
        dob: "",
        level_id: null,
        education_id: null,
        year_id: null,
        password: "",
        current_password: "",
      },
      editErrors: {},
      levels: [],
      years: [],
      avatarFile: null,
      showPasswordFields: false,
      showCurrentPassword: false,
      showNewPassword: false,
      appVersion: "1.0.0",
      // Phase 2: Settings Options
      currentLanguage: i18n.global.locale.value || 'ar',
      currentTheme: localStorage.getItem('theme') || 'light',
      deviceQrCodeUrl: null,
      aboutContent: null,
      aboutLoading: false,
      // Phase 3: FAQ & Contact
      faqItems: [],
      faqLoading: false,
      expandedFaq: null,
      contactForm: {
        fname: '',
        mobile: '',
        email: '',
        message: '',
      },
      contactSubmitting: false,
      // Phase 4: Chat Integration
      chatSettings: {
        allow_message_requests: true,
        message_notification: true,
      },
      chatSettingsLoading: false,
      chatSettingsSaving: false,
      chatShareableId: null,
      chatQrCodeUrl: null,
      chatQrLoading: false,
      chatUser: null,
    };
  },
  computed: {
    filteredEducations() {
      if (this.editForm.level_id) {
        const level = this.levels.find((l) => l.id === this.editForm.level_id);
        return level ? level.educations : [];
      }
      return [];
    },
    userAvatarUrl() {
      // Use user_image_url if available (new API)
      if (this.student.user_image_url) {
        return this.student.user_image_url;
      }
      // Fallback: build URL from user_img filename
      if (this.student.user_img) {
        return `${userImagePath}${this.student.user_img}`;
      }
      // Default avatar
      return '/assets/img/user.png';
    },
    currentLanguageLabel() {
      return this.currentLanguage === 'ar' ? 'العربية' : 'English';
    },
    currentThemeLabel() {
      const labels = {
        light: this.$t('settings.theme_light'),
        dark: this.$t('settings.theme_dark'),
        system: this.$t('settings.theme_system'),
      };
      return labels[this.currentTheme] || labels.light;
    },
  },
  mounted() {
    this.fetchProfile();
    this.fetchInfoData();
    this.fetchDevices();
    this.isMasterDevice();
  },
  methods: {
    async fetchProfile() {
      try {
        const response = await fetch(`${apiUrl}/show/profile`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${this.token}`,
            "Content-Type": "application/json",
          },
        });
        if (response.status === 302 || response.redirected || !response.ok) {
          this.isLoggedIn = false;
          this.isLoading = false;
        } else {
          const data = await response.json();
          // Handle user data
          const user = data.user || data;
          this.student = {
            ...user,
            // Map IDs from nested objects
            level_id: user.level?.id || null,
            education_id: user.education?.id || null,
            year_id: user.year?.id || null,
          };
          // Handle info data (level, education, year names from nested objects)
          this.profileInfo = {
            level: user.level?.name || null,
            education: user.education?.name || null,
            year: user.year?.name || null,
          };
          // Store levels from user.level.educations if available
          if (user.level?.educations) {
            // We'll fetch full levels from get-info-data instead
          }
          this.isLoggedIn = true;
          this.isLoading = false;
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
        this.isLoading = false;
      } finally {
        this.loading = false;
      }
    },
    async fetchInfoData() {
      try {
        const response = await fetch(`${apiUrl}/get-info-data`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });
        if (response.ok) {
          const data = await response.json();
          this.levels = data.levels || [];
          this.years = data.years || [];
        }
      } catch (error) {
        console.error("Error fetching info data:", error);
      }
    },
    handleImageError(event) {
      event.target.src = "/assets/img/user.png";
    },
    triggerAvatarUpload() {
      this.$refs.avatarInput.click();
    },
    async handleAvatarChange(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/bmp', 'image/tiff'];
      if (!allowedTypes.includes(file.type)) {
        Swal.fire({
          icon: "error",
          title: this.$t("settings.invalid_image_type"),
          text: this.$t("auth.image_requirements"),
        });
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        Swal.fire({
          icon: "error",
          title: this.$t("settings.image_too_large"),
          text: this.$t("settings.max_image_size"),
        });
        return;
      }

      // Upload avatar immediately
      await this.uploadAvatar(file);
    },
    async uploadAvatar(file) {
      try {
        const formData = new FormData();
        // Always send required fields along with the image
        formData.append("name", this.student.fname || this.student.name || "");
        formData.append("lname", this.student.lname || "");
        if (this.student.email) {
          formData.append("email", this.student.email);
        }
        if (this.student.dob) {
          formData.append("dob", this.student.dob);
        }
        formData.append("level_id", this.student.level_id || "");
        formData.append("education_id", this.student.education_id || "");
        formData.append("year_id", this.student.year_id || "");
        formData.append("user_img", file);

        const response = await fetch(`${apiUrl}/update/profile`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
          body: formData,
        });

        if (response.ok) {
          const data = await response.json();
          // Update student with new image URL
          const user = data.user || data;
          if (user) {
            this.student = { 
              ...this.student, 
              ...user,
              level_id: user.level?.id || this.student.level_id,
              education_id: user.education?.id || this.student.education_id,
              year_id: user.year?.id || this.student.year_id,
            };
          }
          Swal.fire({
            icon: "success",
            title: this.$t("settings.photo_updated"),
            timer: 1500,
            showConfirmButton: false,
          });
        } else {
          const errorData = await response.json();
          let errorMessage = "Failed to upload";
          if (errorData.errors) {
            errorMessage = Object.values(errorData.errors).flat().join("\n");
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
          throw new Error(errorMessage);
        }
      } catch (error) {
        console.error("Error uploading avatar:", error);
        Swal.fire({
          icon: "error",
          title: this.$t("common.error"),
          text: error.message || this.$t("settings.photo_upload_error"),
        });
      }
    },
    startEditing() {
      this.isEditing = true;
      this.editErrors = {};
      this.showPasswordFields = false;
      this.showCurrentPassword = false;
      this.showNewPassword = false;
      // Populate form with current data
      this.editForm = {
        name: this.student.fname || this.student.name || "",
        lname: this.student.lname || "",
        email: this.student.email || "",
        dob: this.student.dob || "",
        level_id: this.student.level_id || null,
        education_id: this.student.education_id || null,
        year_id: this.student.year_id || null,
        password: "",
        current_password: "",
      };
    },
    cancelEditing() {
      this.isEditing = false;
      this.editErrors = {};
      this.showPasswordFields = false;
      this.showCurrentPassword = false;
      this.showNewPassword = false;
      this.editForm = {
        name: "",
        lname: "",
        email: "",
        dob: "",
        level_id: null,
        education_id: null,
        year_id: null,
        password: "",
        current_password: "",
      };
    },
    onLevelChange() {
      this.editForm.education_id = null;
    },
    validateEditForm() {
      const errors = {};
      
      if (!this.editForm.name) {
        errors.name = this.$t("settings.name_required");
      }
      
      if (this.editForm.email && !/\S+@\S+\.\S+/.test(this.editForm.email)) {
        errors.email = this.$t("settings.invalid_email");
      }
      
      // Password validation only when toggle is on
      if (this.showPasswordFields) {
        if (this.editForm.password && !this.editForm.current_password) {
          errors.current_password = this.$t("settings.current_password_required");
        }
        if (this.editForm.current_password && !this.editForm.password) {
          errors.password = this.$t("settings.new_password_required");
        }
      }
      
      this.editErrors = errors;
      return Object.keys(errors).length === 0;
    },
    async saveProfile() {
      if (!this.validateEditForm()) return;

      this.saving = true;

      try {
        const formData = new FormData();
        formData.append("name", this.editForm.name);
        formData.append("lname", this.editForm.lname);
        
        if (this.editForm.email) {
          formData.append("email", this.editForm.email);
        }
        if (this.editForm.dob) {
          formData.append("dob", this.editForm.dob);
        }
        if (this.editForm.level_id) {
          formData.append("level_id", this.editForm.level_id);
        }
        if (this.editForm.education_id) {
          formData.append("education_id", this.editForm.education_id);
        }
        if (this.editForm.year_id) {
          formData.append("year_id", this.editForm.year_id);
        }
        
        // Only send password fields if password is provided and toggle is on
        if (this.showPasswordFields && this.editForm.password) {
          formData.append("password", this.editForm.password);
          formData.append("current_password", this.editForm.current_password);
        }

        const response = await fetch(`${apiUrl}/update/profile`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
          body: formData,
        });

        if (response.ok) {
          const data = await response.json();
          // Update student data
          const user = data.user || data;
          if (user) {
            this.student = {
              ...this.student,
              ...user,
              level_id: user.level?.id || this.student.level_id,
              education_id: user.education?.id || this.student.education_id,
              year_id: user.year?.id || this.student.year_id,
            };
            // Update profile info from nested objects
            this.profileInfo = {
              level: user.level?.name || this.profileInfo.level,
              education: user.education?.name || this.profileInfo.education,
              year: user.year?.name || this.profileInfo.year,
            };
          }
          
          this.isEditing = false;
          Swal.fire({
            icon: "success",
            title: this.$t("settings.profile_updated"),
            timer: 1500,
            showConfirmButton: false,
          });
        } else {
          const errorData = await response.json();
          let errorMessage = "Failed to update profile";
          if (errorData.errors) {
            errorMessage = Object.values(errorData.errors).flat().join("\n");
            // Map backend errors to form errors
            this.editErrors = Object.keys(errorData.errors).reduce((acc, key) => {
              acc[key] = Array.isArray(errorData.errors[key])
                ? errorData.errors[key].join(" ")
                : errorData.errors[key];
              return acc;
            }, {});
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
          throw new Error(errorMessage);
        }
      } catch (error) {
        console.error("Error saving profile:", error);
        Swal.fire({
          icon: "error",
          title: this.$t("common.error"),
          text: error.message || this.$t("settings.update_error"),
        });
      } finally {
        this.saving = false;
      }
    },
    async fetchDevices() {
      try {
        const data = {
          user_device_id: deviceId, // Adjust if necessary
        };
        const response = await fetch(`${apiUrl}/devices`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${this.token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        });
        if (response.ok) {
          const responseData = await response.json();
          this.devices = responseData.devices || [];
          await this.generateQRCodes(); // توليد رموز QR بعد جلب الأجهزة
        } else {
          console.error("Failed to fetch device details");
        }
      } catch (error) {
        console.error("Error fetching device details:", error);
      } finally {
        this.loading = false;
      }
    },
    async isMasterDevice() {
      try {
        const data = {
          device_id: deviceId, // Adjust if necessary
        };
        const response = await fetch(`${apiUrl}/devices/is-master`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${this.token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        });
        if (response.ok) {
          const responseData = await response.json();
          // Check the result and set isMaster accordingly
          this.isMaster = responseData.result === true;
        } else {
          console.error("Failed to fetch device details");
          this.isMaster = false; // Default to false if the request fails
        }
      } catch (error) {
        console.error("Error fetching device details:", error);
        this.isMaster = false; // Default to false if an error occurs
      } finally {
        this.loading = false;
      }
    },
    async generateQRCodes() {
      for (let device of this.devices) {
        const qrCodeUrl = await this.generateQRCodeUrl(device.device_id);
        this.qrCodeUrls = { ...this.qrCodeUrls, [device.device_id]: qrCodeUrl };
      }
    },
    async generateQRCodeUrl(deviceId) {
      try {
        return await QRCode.toDataURL(deviceId, { errorCorrectionLevel: "H" });
      } catch (error) {
        console.error("Error generating QR code:", error);
        return "";
      }
    },
    async handleDelete(device_id) {
      const result = await Swal.fire({
        title: "هل أنت متأكد من  حذف الجهاز",
        text: " ",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3ddc97",
        cancelButtonColor: "#f46a6a",
        cancelButtonText: "لا",
        confirmButtonText: "نعم",
      });

      if (result.isConfirmed) {
        try {
          const data = {
            id: device_id,
            user_device_id: deviceId, // Adjust if necessary
          };
          const response = await fetch(`${apiUrl}/devices/delete-device`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${this.token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(data),
          });
          if (response.ok) {
            const responseData = await response.json();
            // Check the result and set isMaster accordingly
            Swal.fire({
              icon: "success",
              title: "تم حذف الجهاز بنجاح",
              text: "",
            }).then((result) => {
              if (result.isConfirmed) {
                location.reload();
              }
            });
          } else {
            Swal.fire({
              icon: "error",
              title: "حدثت مشكلة",
              text: "",
            }).then((result) => {
              if (result.isConfirmed) {
                location.reload();
              }
            });
          }
        } catch (error) {
          console.error("Error fetching device details:", error);
          this.isMaster = false; // Default to false if an error occurs
        } finally {
          this.loading = false;
        }
      }
    },
    async handleLogout() {
      const result = await Swal.fire({
        title: this.$t("settings.logout_confirm_title"),
        text: this.$t("settings.logout_confirm_text"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#dc3545",
        cancelButtonColor: "#6c757d",
        confirmButtonText: this.$t("common.logout"),
        cancelButtonText: this.$t("common.cancel"),
      });

      if (result.isConfirmed) {
        this.$router.push("/logout");
      }
    },
    // Phase 2: Settings Options Methods
    changeLanguage(lang) {
      if (this.currentLanguage === lang) {
        bootstrap.Modal.getInstance(document.getElementById('languageModal'))?.hide();
        return;
      }
      switchLocale(lang);
      this.currentLanguage = lang;
      bootstrap.Modal.getInstance(document.getElementById('languageModal'))?.hide();
    },
    changeTheme(theme) {
      this.currentTheme = theme;
      localStorage.setItem('theme', theme);
      
      // Apply theme
      const root = document.documentElement;
      if (theme === 'system') {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        root.setAttribute('data-bs-theme', prefersDark ? 'dark' : 'light');
      } else {
        root.setAttribute('data-bs-theme', theme);
      }
      
      bootstrap.Modal.getInstance(document.getElementById('themeModal'))?.hide();
    },
    async showDeleteSessionsConfirm() {
      const result = await Swal.fire({
        title: this.$t("settings.delete_sessions_title"),
        text: this.$t("settings.delete_sessions_text"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#dc3545",
        cancelButtonColor: "#6c757d",
        confirmButtonText: this.$t("common.delete"),
        cancelButtonText: this.$t("common.cancel"),
      });

      if (result.isConfirmed) {
        await this.deleteAllSessions();
      }
    },
    async deleteAllSessions() {
      try {
        const response = await fetch(`${apiUrl}/delete-all-sessions`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${this.token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            sigDate: new Date().toISOString(),
            OS: "windows",
          }),
        });

        if (response.ok) {
          Swal.fire({
            icon: "success",
            title: this.$t("settings.sessions_deleted"),
            timer: 1500,
            showConfirmButton: false,
          }).then(() => {
            this.$router.push("/logout");
          });
        } else {
          throw new Error("Failed to delete sessions");
        }
      } catch (error) {
        console.error("Error deleting sessions:", error);
        Swal.fire({
          icon: "error",
          title: this.$t("common.error"),
          text: this.$t("settings.delete_sessions_error"),
        });
      }
    },
    async generateDeviceQrCode() {
      try {
        // Generate QR with device ID and name (matching Flutter format)
        const deviceName = navigator.userAgent.split(' ')[0] || 'Web Browser';
        const qrData = `${deviceId}//${deviceName}`;
        this.deviceQrCodeUrl = await QRCode.toDataURL(qrData, { 
          errorCorrectionLevel: "H",
          width: 256,
        });
      } catch (error) {
        console.error("Error generating device QR code:", error);
      }
    },
    async fetchAboutContent() {
      if (this.aboutContent) return; // Already fetched
      
      this.aboutLoading = true;
      try {
        const response = await fetch(`${apiUrl}/aboutus`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.about && data.about.length > 0) {
            this.aboutContent = data.about[0].one_text || null;
          }
        }
      } catch (error) {
        console.error("Error fetching about content:", error);
      } finally {
        this.aboutLoading = false;
      }
    },
    async shareApp() {
      const shareText = this.$t("settings.share_app_text");
      const shareUrl = "https://play.google.com/store/apps/details?id=com.academia.ly";
      const fullText = `${shareText}\n${shareUrl}`;

      if (navigator.share) {
        try {
          await navigator.share({
            title: this.$t("common.app_name"),
            text: shareText,
            url: shareUrl,
          });
        } catch (error) {
          if (error.name !== 'AbortError') {
            this.copyToClipboard(fullText);
          }
        }
      } else {
        this.copyToClipboard(fullText);
      }
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        Swal.fire({
          icon: "success",
          title: this.$t("settings.link_copied"),
          timer: 1500,
          showConfirmButton: false,
        });
      }).catch(() => {
        Swal.fire({
          icon: "error",
          title: this.$t("common.error"),
        });
      });
    },
    // Phase 3: FAQ & Contact Methods
    async fetchFaqItems() {
      if (this.faqItems.length > 0) return; // Already fetched
      
      this.faqLoading = true;
      try {
        const response = await fetch(`${apiUrl}/user/faq`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const data = await response.json();
          this.faqItems = data.faq || [];
        }
      } catch (error) {
        console.error("Error fetching FAQ:", error);
      } finally {
        this.faqLoading = false;
      }
    },
    toggleFaq(index) {
      this.expandedFaq = this.expandedFaq === index ? null : index;
    },
    copyEmail() {
      navigator.clipboard.writeText("info@academia.ly").then(() => {
        Swal.fire({
          icon: "success",
          title: this.$t("settings.email_copied"),
          timer: 1500,
          showConfirmButton: false,
        });
      });
    },
    callPhone() {
      window.location.href = "tel:+218922938790";
    },
    async submitContactForm() {
      if (!this.contactForm.fname || !this.contactForm.mobile || !this.contactForm.message) {
        Swal.fire({
          icon: "warning",
          title: this.$t("settings.fill_required_fields"),
        });
        return;
      }

      this.contactSubmitting = true;
      try {
        const response = await fetch(`${apiUrl}/contactus`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(this.contactForm),
        });

        if (response.ok) {
          Swal.fire({
            icon: "success",
            title: this.$t("settings.message_sent"),
            timer: 2000,
            showConfirmButton: false,
          });
          // Reset form
          this.contactForm = {
            fname: '',
            mobile: '',
            email: '',
            message: '',
          };
          bootstrap.Modal.getInstance(document.getElementById('contactModal'))?.hide();
        } else {
          throw new Error("Failed to send message");
        }
      } catch (error) {
        console.error("Error sending contact form:", error);
        Swal.fire({
          icon: "error",
          title: this.$t("common.error"),
          text: this.$t("settings.message_send_error"),
        });
      } finally {
        this.contactSubmitting = false;
      }
    },
    // Phase 4: Chat Methods
    async fetchChatSettings() {
      this.chatSettingsLoading = true;
      try {
        const response = await fetch(`${apiUrl}/chat/settings`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${this.token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const result = await response.json();
          const data = result.data || result;
          this.chatSettings = {
            allow_message_requests: data.allow_message_requests ?? true,
            message_notification: data.message_notification ?? true,
          };
        }
      } catch (error) {
        console.error("Error fetching chat settings:", error);
      } finally {
        this.chatSettingsLoading = false;
      }
    },
    async saveChatSettings() {
      this.chatSettingsSaving = true;
      try {
        const response = await fetch(`${apiUrl}/chat/settings`, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${this.token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(this.chatSettings),
        });

        if (response.ok) {
          Swal.fire({
            icon: "success",
            title: this.$t("settings.settings_saved"),
            timer: 1500,
            showConfirmButton: false,
          });
          bootstrap.Modal.getInstance(document.getElementById('chatSettingsModal'))?.hide();
        } else {
          throw new Error("Failed to save settings");
        }
      } catch (error) {
        console.error("Error saving chat settings:", error);
        Swal.fire({
          icon: "error",
          title: this.$t("common.error"),
          text: this.$t("settings.settings_save_error"),
        });
      } finally {
        this.chatSettingsSaving = false;
      }
    },
    async fetchChatShareableId() {
      this.chatQrLoading = true;
      try {
        const response = await fetch(`${apiUrl}/chat/shareable-id`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${this.token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const result = await response.json();
          const data = result.data || result;
          this.chatShareableId = data.shareable_id || null;
          this.chatUser = data.user || null;
          if (this.chatShareableId) {
            this.chatQrCodeUrl = await QRCode.toDataURL(this.chatShareableId, {
              errorCorrectionLevel: "H",
              width: 256,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching chat shareable ID:", error);
      } finally {
        this.chatQrLoading = false;
      }
    },
    copyChatId() {
      if (this.chatShareableId) {
        navigator.clipboard.writeText(this.chatShareableId).then(() => {
          Swal.fire({
            icon: "success",
            title: this.$t("settings.id_copied"),
            timer: 1500,
            showConfirmButton: false,
          });
        });
      }
    },
    handleChatAvatarError(e) {
      e.target.src = "/assets/img/acad-icon.png";
    },
  },
  watch: {},
};
</script>

<style scoped>
/* ========== Toolbar ========== */
#kt_app_toolbar {
  background: linear-gradient(135deg, #ffffff 0%, #f8fffe 100%);
  border-bottom: 1px solid rgba(0, 196, 68, 0.1);
  box-shadow: 0 4px 20px rgba(0, 196, 68, 0.05);
}
.page-heading {
  background: linear-gradient(135deg, #00c444 0%, #00a43c 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-size: 1.5rem !important;
}

/* ========== Loader ========== */
.loader {
  border-radius: 24px !important;
  background: linear-gradient(145deg, #f8fffe 0%, #f0fff5 100%) !important;
  border: 1px solid rgba(0, 196, 68, 0.1);
  box-shadow: 0 8px 32px rgba(0, 196, 68, 0.08);
  animation: loaderPulse 1.5s ease-in-out infinite;
}
@keyframes loaderPulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

/* ========== Not Login Card ========== */
.not-login {
  border-radius: 24px;
  background: linear-gradient(145deg, #ffffff 0%, #fafffe 100%);
  border: 1px solid rgba(0, 196, 68, 0.1);
  box-shadow: 0 20px 60px rgba(0, 196, 68, 0.1);
  padding: 60px !important;
}
.not-login .icon {
  font-size: 5rem;
  color: #00c444;
  display: block;
  margin-bottom: 20px;
  animation: iconFloat 3s ease-in-out infinite;
}
@keyframes iconFloat {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}
.not-login h3 {
  color: #1a1a2e;
  font-weight: 700;
}
.not-login .btn-success {
  background: linear-gradient(135deg, #00c444 0%, #00a43c 100%);
  border: none;
  border-radius: 999px;
  padding: 12px 32px;
  font-weight: 600;
  box-shadow: 0 8px 25px rgba(0, 196, 68, 0.3);
  transition: all 0.3s ease;
}
.not-login .btn-success:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 35px rgba(0, 196, 68, 0.4);
}

/* ========== Sidebar Card ========== */
.card.mb-5.mb-xl-8 {
  border-radius: 24px !important;
  background: linear-gradient(145deg, #ffffff 0%, #fafffe 100%);
  border: 1px solid rgba(0, 196, 68, 0.1) !important;
  box-shadow: 0 10px 40px rgba(0, 196, 68, 0.08) !important;
  overflow: hidden;
  position: relative;
}
.card.mb-5.mb-xl-8::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 5px;
  background: linear-gradient(90deg, #00c444, #00e050, #00c444);
  background-size: 200% 100%;
  animation: gradientSlide 3s linear infinite;
}
@keyframes gradientSlide {
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 200% 50%;
  }
}

/* ========== Avatar ========== */
.symbol-100px {
  position: relative;
}
.symbol-100px img {
  border: 4px solid rgba(0, 196, 68, 0.2);
  box-shadow: 0 8px 30px rgba(0, 196, 68, 0.2);
  transition: all 0.3s ease;
}
.symbol-100px:hover img {
  transform: scale(1.05);
  border-color: rgba(0, 196, 68, 0.4);
}

/* ========== User Name ========== */
.fs-3.text-gray-800 {
  color: #1a1a2e !important;
  font-weight: 700 !important;
  transition: color 0.3s ease;
}
.fs-3.text-gray-800:hover {
  color: #00c444 !important;
}

/* ========== Badge ========== */
.badge-light-success {
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e2 100%) !important;
  color: #00a43c !important;
  font-weight: 600;
  padding: 10px 20px;
  border-radius: 999px;
  border: 1px solid rgba(0, 196, 68, 0.2);
}

/* ========== Avatar Edit ========== */
.avatar-wrapper {
  position: relative;
}

.avatar-edit-btn {
  position: absolute;
  bottom: 5px;
  right: 5px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #00c444 0%, #00a43c 100%);
  border: 3px solid #fff;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  box-shadow: 0 4px 12px rgba(0, 196, 68, 0.4);
  transition: all 0.3s ease;
}

.avatar-edit-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 16px rgba(0, 196, 68, 0.5);
}

.hidden-input {
  display: none;
}

/* ========== Card Header ========== */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 20px 24px;
}

.card-header .card-title h3 {
  color: #1a1a2e;
  font-size: 1.25rem;
}

/* ========== Edit Form ========== */
.form-label {
  color: #374151;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 8px;
}

.form-control {
  height: 48px;
  border: 2px solid #e8e8e8;
  border-radius: 12px;
  padding: 10px 14px;
  transition: all 0.3s ease;
}

.form-control:focus {
  border-color: #00c444;
  box-shadow: 0 0 0 4px rgba(0, 196, 68, 0.12);
  outline: none;
}

select.form-control {
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%2300c444' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14L2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: left 14px center;
  padding-left: 36px;
}

.separator {
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(0, 196, 68, 0.3), transparent);
}

/* ========== Profile View Sections ========== */
.profile-view {
  padding: 10px 0;
}

.info-section {
  margin-bottom: 28px;
  padding-bottom: 24px;
  border-bottom: 1px solid rgba(0, 196, 68, 0.1);
}

.info-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #00a43c;
  font-weight: 700;
  font-size: 1rem;
  margin-bottom: 20px;
}

.section-title i {
  font-size: 1.1rem;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.info-item {
  background: linear-gradient(145deg, #f8fffe 0%, #f0fff5 100%);
  border-radius: 12px;
  padding: 16px;
  border: 1px solid rgba(0, 196, 68, 0.08);
  transition: all 0.3s ease;
}

.info-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 196, 68, 0.1);
}

.info-item.full-width {
  grid-column: span 2;
}

.info-label {
  display: block;
  color: #666;
  font-size: 0.85rem;
  font-weight: 500;
  margin-bottom: 6px;
}

.info-value {
  color: #1a1a2e;
  font-size: 1rem;
  font-weight: 600;
}

.email-value {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.verified-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e2 100%);
  color: #00a43c;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  border: 1px solid rgba(0, 196, 68, 0.2);
}

.verified-badge i {
  color: #00c444;
}

.unverified-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #fff5f5 0%, #ffe8e8 100%);
  color: #dc3545;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  border: 1px solid rgba(220, 53, 69, 0.2);
}

/* ========== Edit Form Sections ========== */
.edit-form {
  padding: 10px 0;
}

.form-section {
  margin-bottom: 28px;
  padding: 20px;
  background: linear-gradient(145deg, #fafffe 0%, #f5fff9 100%);
  border-radius: 16px;
  border: 1px solid rgba(0, 196, 68, 0.1);
}

.form-section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #00a43c;
  font-weight: 700;
  font-size: 1rem;
  margin-bottom: 20px;
}

.form-section-title i {
  font-size: 1.1rem;
}

/* ========== Password Toggle Switch ========== */
.password-section {
  background: linear-gradient(145deg, #fff9f0 0%, #fff5e8 100%);
  border-color: rgba(255, 193, 7, 0.2);
}

.password-toggle-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.switch-container {
  position: relative;
  display: inline-block;
  width: 52px;
  height: 28px;
  cursor: pointer;
}

.switch-input {
  opacity: 0;
  width: 0;
  height: 0;
}

.switch-slider {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #ccc;
  border-radius: 28px;
  transition: all 0.3s ease;
}

.switch-slider::before {
  content: "";
  position: absolute;
  height: 22px;
  width: 22px;
  left: 3px;
  bottom: 3px;
  background: white;
  border-radius: 50%;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

.switch-input:checked + .switch-slider {
  background: linear-gradient(135deg, #00c444 0%, #00a43c 100%);
}

.switch-input:checked + .switch-slider::before {
  transform: translateX(24px);
}

.password-fields {
  padding-top: 16px;
  border-top: 1px dashed rgba(0, 196, 68, 0.2);
  margin-top: 16px;
}

/* ========== Password Input Wrapper ========== */
.password-input-wrapper {
  position: relative;
}

.password-input-wrapper .form-control {
  padding-left: 45px;
}

.password-toggle-btn {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 5px;
  transition: color 0.3s ease;
}

.password-toggle-btn:hover {
  color: #00c444;
}

/* ========== Slide Fade Transition ========== */
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s ease-in;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* ========== Responsive ========== */
@media (max-width: 768px) {
  .info-grid {
    grid-template-columns: 1fr;
  }
  
  .info-item.full-width {
    grid-column: span 1;
  }
  
  .password-toggle-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
}

/* ========== Tabs ========== */
.nav-custom.nav-tabs {
  background: linear-gradient(145deg, #f8fffe 0%, #f0fff5 100%);
  border-radius: 16px;
  padding: 8px;
  border: 1px solid rgba(0, 196, 68, 0.1);
}
.nav-custom .nav-link {
  border-radius: 12px;
  padding: 12px 24px;
  font-weight: 600;
  color: #666;
  transition: all 0.3s ease;
  border: none !important;
}
.nav-custom .nav-link:hover {
  background: rgba(0, 196, 68, 0.1);
  color: #00c444;
}
.nav-custom .nav-link.active {
  background: linear-gradient(135deg, #00c444 0%, #00a43c 100%) !important;
  color: #fff !important;
  box-shadow: 0 4px 15px rgba(0, 196, 68, 0.3);
}

/* ========== Content Card ========== */
.card.pt-4.mb-6 {
  border-radius: 24px !important;
  background: linear-gradient(145deg, #ffffff 0%, #fafffe 100%);
  border: 1px solid rgba(0, 196, 68, 0.1) !important;
  box-shadow: 0 10px 40px rgba(0, 196, 68, 0.08) !important;
}

/* ========== Info Rows ========== */
.row.mb-7 {
  padding: 16px 0;
  border-bottom: 1px solid rgba(0, 196, 68, 0.08);
  transition: all 0.3s ease;
}
.row.mb-7:hover {
  background: linear-gradient(135deg, #f8fffe 0%, #f0fff5 100%);
  border-radius: 12px;
  margin: 0 -12px;
  padding: 16px 12px;
}
.row.mb-7:last-child {
  border-bottom: none;
}
.row.mb-7 label {
  color: #666 !important;
  font-weight: 600;
}
.row.mb-7 .fw-semibold,
.row.mb-7 .fw-bold {
  color: #1a1a2e !important;
}

/* ========== Status Badge ========== */
.badge-success {
  background: linear-gradient(135deg, #00c444 0%, #00a43c 100%) !important;
  border-radius: 999px;
  padding: 6px 14px;
  font-weight: 600;
}
.badge-danger {
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%) !important;
  border-radius: 999px;
  padding: 6px 14px;
  font-weight: 600;
}

/* ========== Devices Tab ========== */
.add-box {
  background: linear-gradient(145deg, #f0fff5 0%, #e8fff1 100%);
  border-radius: 16px;
  border: 1px dashed rgba(0, 196, 68, 0.3);
  margin: 16px;
  text-align: center;
}
.add-box .btn-success {
  background: linear-gradient(135deg, #00c444 0%, #00a43c 100%);
  border: none;
  border-radius: 12px;
  padding: 12px 24px;
  font-weight: 600;
  box-shadow: 0 4px 15px rgba(0, 196, 68, 0.3);
  transition: all 0.3s ease;
}
.add-box .btn-success:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 196, 68, 0.4);
}

/* ========== Device Card ========== */
.device-card {
  background: linear-gradient(145deg, #ffffff 0%, #fafffe 100%);
  border: 1px solid rgba(0, 196, 68, 0.1);
  border-radius: 20px;
  padding: 20px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}
.device-card::before {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, #00c444 0%, #00e050 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}
.device-card:hover::before {
  opacity: 1;
}
.device-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 30px rgba(0, 196, 68, 0.12);
  border-color: rgba(0, 196, 68, 0.25);
}
.device-card h5 {
  color: #1a1a2e;
  font-weight: 700;
  margin-bottom: 10px;
}
.device-card .badge-primary {
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%) !important;
  border-radius: 999px;
  padding: 4px 12px;
}
.device-card .badge-info {
  background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%) !important;
  border-radius: 999px;
  padding: 4px 12px;
}
.device-card .btn-dark {
  background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
  border: none;
  border-radius: 10px;
  padding: 8px 16px;
  transition: all 0.3s ease;
}
.device-card .btn-dark:hover {
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  transform: scale(1.05);
}
.device-card img {
  border-radius: 12px;
  border: 2px solid rgba(0, 196, 68, 0.15);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}
.device-card hr {
  border-color: rgba(0, 196, 68, 0.1);
  margin-top: 20px;
}

/* ========== NEW Devices Container ========== */
.devices-container {
  background: linear-gradient(145deg, #ffffff 0%, #fafffe 100%);
  border-radius: 24px;
  border: 1px solid rgba(0, 196, 68, 0.1);
  box-shadow: 0 10px 40px rgba(0, 196, 68, 0.08);
  overflow: hidden;
}

/* Devices Header */
.devices-header {
  background: linear-gradient(135deg, #f0fff5 0%, #e8fff1 100%);
  padding: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(0, 196, 68, 0.1);
  flex-wrap: wrap;
  gap: 16px;
}
.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}
.header-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: linear-gradient(135deg, #00c444 0%, #00a43c 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 25px rgba(0, 196, 68, 0.3);
}
.header-icon i {
  font-size: 1.5rem;
  color: #fff;
}
.header-text h4 {
  margin: 0;
  color: #1a1a2e;
  font-weight: 700;
  font-size: 1.25rem;
}
.header-text p {
  margin: 4px 0 0;
  color: #666;
  font-size: 0.9rem;
}

/* Add Device Button */
.btn-add-device {
  display: flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #00c444 0%, #00a43c 100%);
  color: #fff;
  border: none;
  border-radius: 14px;
  padding: 14px 24px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(0, 196, 68, 0.3);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}
.btn-add-device::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: left 0.5s ease;
}
.btn-add-device:hover::before {
  left: 100%;
}
.btn-add-device:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 30px rgba(0, 196, 68, 0.4);
}
.btn-add-device i {
  font-size: 1.2rem;
}

/* Devices Grid */
.devices-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
  padding: 24px;
}

/* New Device Card */
.device-card-new {
  background: linear-gradient(145deg, #ffffff 0%, #fafffe 100%);
  border: 1px solid rgba(0, 196, 68, 0.1);
  border-radius: 20px;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
}
.device-card-new::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #00c444, #00e050, #00c444);
  background-size: 200% 100%;
  opacity: 0;
  transition: opacity 0.3s ease;
}
.device-card-new:hover::before {
  opacity: 1;
  animation: gradientSlide 2s linear infinite;
}
.device-card-new:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 50px rgba(0, 196, 68, 0.15);
  border-color: rgba(0, 196, 68, 0.25);
}

/* Device Card Header */
.device-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background: linear-gradient(135deg, #f8fffe 0%, #f0fff5 100%);
  border-bottom: 1px solid rgba(0, 196, 68, 0.08);
}
.device-icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid rgba(0, 196, 68, 0.2);
}
.device-icon i {
  font-size: 1.4rem;
  color: #00c444;
}
.device-status {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
}
.device-status.active {
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e2 100%);
  color: #00a43c;
}
.device-status.inactive {
  background: linear-gradient(135deg, #fff5f5 0%, #ffe8e8 100%);
  color: #c82333;
}
.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
  animation: statusPulse 2s ease-in-out infinite;
}
@keyframes statusPulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(0.8);
  }
}

/* Device Card Body */
.device-card-body {
  padding: 20px;
}
.device-name {
  margin: 0 0 16px;
  color: #1a1a2e;
  font-weight: 700;
  font-size: 1.1rem;
}
.device-info {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.info-item {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #666;
  font-size: 0.9rem;
}
.info-item i {
  width: 20px;
  color: #00c444;
}

/* Device Card Footer */
.device-card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: linear-gradient(135deg, #f8fffe 0%, #f0fff5 100%);
  border-top: 1px solid rgba(0, 196, 68, 0.08);
}
.qr-code-mini {
  width: 60px;
  height: 60px;
  border-radius: 10px;
  overflow: hidden;
  border: 2px solid rgba(0, 196, 68, 0.15);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}
.qr-code-mini img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.btn-delete-device {
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #dc3545;
  border: 1px solid rgba(220, 53, 69, 0.2);
  border-radius: 10px;
  padding: 10px 16px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.3s ease;
}
.btn-delete-device:hover {
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: #fff;
  border-color: transparent;
  transform: scale(1.05);
}

/* Devices Loading */
.devices-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
}
.loading-spinner {
  margin-bottom: 16px;
}
.devices-loading p {
  color: #666;
  font-weight: 500;
}

/* Devices Empty */
.devices-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}
.empty-icon {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f0fff5 0%, #e8fff1 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  border: 3px dashed rgba(0, 196, 68, 0.3);
}
.empty-icon i {
  font-size: 2.5rem;
  color: rgba(0, 196, 68, 0.4);
}
.devices-empty h5 {
  color: #1a1a2e;
  font-weight: 700;
  margin-bottom: 8px;
}
.devices-empty p {
  color: #666;
  margin-bottom: 24px;
}
.btn-add-first {
  display: flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #00c444 0%, #00a43c 100%);
  color: #fff;
  border: none;
  border-radius: 14px;
  padding: 14px 28px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(0, 196, 68, 0.3);
  transition: all 0.3s ease;
}
.btn-add-first:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 30px rgba(0, 196, 68, 0.4);
}

/* ========== Responsive ========== */
@media (max-width: 768px) {
  .card.mb-5.mb-xl-8,
  .card.pt-4.mb-6 {
    border-radius: 18px !important;
  }
  .nav-custom.nav-tabs {
    flex-direction: column;
  }
  .nav-custom .nav-link {
    margin-bottom: 8px;
  }
  .device-card {
    border-radius: 16px;
  }
  .devices-header {
    flex-direction: column;
    text-align: center;
  }
  .header-content {
    flex-direction: column;
  }
  .devices-grid {
    grid-template-columns: 1fr;
    padding: 16px;
  }
  .device-card-new {
    border-radius: 16px;
  }
}

/* ========== Scrollbar ========== */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
::-webkit-scrollbar-track {
  background: #f0fff5;
  border-radius: 10px;
}
::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #00c444 0%, #00a43c 100%);
  border-radius: 10px;
}
::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #00d44a 0%, #00b844 100%);
}

/* ========== DARK MODE ========== */
[data-bs-theme="dark"] #kt_app_toolbar,
[data-theme="dark"] #kt_app_toolbar,
.dark #kt_app_toolbar {
  background: linear-gradient(135deg, #1a1b1d 0%, #242526 100%);
  border-color: rgba(0, 196, 68, 0.15);
}

[data-bs-theme="dark"] .page-heading,
[data-theme="dark"] .page-heading,
.dark .page-heading {
  background: linear-gradient(135deg, #00e050 0%, #00c444 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

[data-bs-theme="dark"] .loader,
[data-theme="dark"] .loader,
.dark .loader {
  background: linear-gradient(145deg, #242526 0%, #1a1b1d 100%) !important;
  border-color: rgba(0, 196, 68, 0.15);
  color: #00e050 !important;
}

[data-bs-theme="dark"] .not-login,
[data-theme="dark"] .not-login,
.dark .not-login {
  background: linear-gradient(145deg, #242526 0%, #1a1b1d 100%);
  border-color: rgba(0, 196, 68, 0.15);
}
[data-bs-theme="dark"] .not-login h3,
[data-theme="dark"] .not-login h3,
.dark .not-login h3 {
  color: #e4e6eb;
}

[data-bs-theme="dark"] .card.mb-5.mb-xl-8,
[data-bs-theme="dark"] .card.pt-4.mb-6,
[data-theme="dark"] .card.mb-5.mb-xl-8,
[data-theme="dark"] .card.pt-4.mb-6,
.dark .card.mb-5.mb-xl-8,
.dark .card.pt-4.mb-6 {
  background: linear-gradient(145deg, #242526 0%, #1a1b1d 100%);
  border-color: rgba(0, 196, 68, 0.15) !important;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3) !important;
}

[data-bs-theme="dark"] .fs-3.text-gray-800,
[data-theme="dark"] .fs-3.text-gray-800,
.dark .fs-3.text-gray-800 {
  color: #e4e6eb !important;
}

[data-bs-theme="dark"] .badge-light-success,
[data-theme="dark"] .badge-light-success,
.dark .badge-light-success {
  background: linear-gradient(135deg, #1a3d2a 0%, #1e4d35 100%) !important;
  color: #8bffb3 !important;
  border-color: rgba(0, 196, 68, 0.35);
}

[data-bs-theme="dark"] .nav-custom.nav-tabs,
[data-theme="dark"] .nav-custom.nav-tabs,
.dark .nav-custom.nav-tabs {
  background: linear-gradient(145deg, #2d2e30 0%, #242526 100%);
  border-color: rgba(0, 196, 68, 0.15);
}
[data-bs-theme="dark"] .nav-custom .nav-link,
[data-theme="dark"] .nav-custom .nav-link,
.dark .nav-custom .nav-link {
  color: #b0b3b8;
}
[data-bs-theme="dark"] .nav-custom .nav-link:hover,
[data-theme="dark"] .nav-custom .nav-link:hover,
.dark .nav-custom .nav-link:hover {
  background: rgba(0, 196, 68, 0.15);
  color: #00e050;
}

[data-bs-theme="dark"] .row.mb-7,
[data-theme="dark"] .row.mb-7,
.dark .row.mb-7 {
  border-color: rgba(0, 196, 68, 0.1);
}
[data-bs-theme="dark"] .row.mb-7:hover,
[data-theme="dark"] .row.mb-7:hover,
.dark .row.mb-7:hover {
  background: linear-gradient(135deg, #2d2e30 0%, #1a3d2a 100%);
}
[data-bs-theme="dark"] .row.mb-7 label,
[data-theme="dark"] .row.mb-7 label,
.dark .row.mb-7 label {
  color: #b0b3b8 !important;
}
[data-bs-theme="dark"] .row.mb-7 .fw-semibold,
[data-bs-theme="dark"] .row.mb-7 .fw-bold,
[data-theme="dark"] .row.mb-7 .fw-semibold,
[data-theme="dark"] .row.mb-7 .fw-bold,
.dark .row.mb-7 .fw-semibold,
.dark .row.mb-7 .fw-bold {
  color: #e4e6eb !important;
}

[data-bs-theme="dark"] .add-box,
[data-theme="dark"] .add-box,
.dark .add-box {
  background: linear-gradient(145deg, #1a3d2a 0%, #1e4d35 100%);
  border-color: rgba(0, 196, 68, 0.4);
}

[data-bs-theme="dark"] .device-card,
[data-theme="dark"] .device-card,
.dark .device-card {
  background: linear-gradient(145deg, #242526 0%, #1a1b1d 100%);
  border-color: rgba(0, 196, 68, 0.15);
}
[data-bs-theme="dark"] .device-card:hover,
[data-theme="dark"] .device-card:hover,
.dark .device-card:hover {
  box-shadow: 0 12px 30px rgba(0, 196, 68, 0.15);
}
[data-bs-theme="dark"] .device-card h5,
[data-theme="dark"] .device-card h5,
.dark .device-card h5 {
  color: #e4e6eb;
}
[data-bs-theme="dark"] .device-card .text-muted,
[data-theme="dark"] .device-card .text-muted,
.dark .device-card .text-muted {
  color: #b0b3b8 !important;
}
[data-bs-theme="dark"] .device-card hr,
[data-theme="dark"] .device-card hr,
.dark .device-card hr {
  border-color: rgba(0, 196, 68, 0.15);
}

/* NEW Devices Container Dark Mode */
[data-bs-theme="dark"] .devices-container,
[data-theme="dark"] .devices-container,
.dark .devices-container {
  background: linear-gradient(145deg, #242526 0%, #1a1b1d 100%);
  border-color: rgba(0, 196, 68, 0.15);
}
[data-bs-theme="dark"] .devices-header,
[data-theme="dark"] .devices-header,
.dark .devices-header {
  background: linear-gradient(135deg, #1a3d2a 0%, #1e4d35 100%);
  border-color: rgba(0, 196, 68, 0.2);
}
[data-bs-theme="dark"] .header-text h4,
[data-theme="dark"] .header-text h4,
.dark .header-text h4 {
  color: #e4e6eb;
}
[data-bs-theme="dark"] .header-text p,
[data-theme="dark"] .header-text p,
.dark .header-text p {
  color: #b0b3b8;
}
[data-bs-theme="dark"] .device-card-new,
[data-theme="dark"] .device-card-new,
.dark .device-card-new {
  background: linear-gradient(145deg, #242526 0%, #1a1b1d 100%);
  border-color: rgba(0, 196, 68, 0.15);
}
[data-bs-theme="dark"] .device-card-new:hover,
[data-theme="dark"] .device-card-new:hover,
.dark .device-card-new:hover {
  box-shadow: 0 20px 50px rgba(0, 196, 68, 0.2);
}
[data-bs-theme="dark"] .device-card-header,
[data-theme="dark"] .device-card-header,
.dark .device-card-header {
  background: linear-gradient(135deg, #2d2e30 0%, #242526 100%);
  border-color: rgba(0, 196, 68, 0.1);
}
[data-bs-theme="dark"] .device-icon,
[data-theme="dark"] .device-icon,
.dark .device-icon {
  background: linear-gradient(135deg, #1a3d2a 0%, #1e4d35 100%);
  border-color: rgba(0, 196, 68, 0.35);
}
[data-bs-theme="dark"] .device-icon i,
[data-theme="dark"] .device-icon i,
.dark .device-icon i {
  color: #00e050;
}
[data-bs-theme="dark"] .device-status.active,
[data-theme="dark"] .device-status.active,
.dark .device-status.active {
  background: linear-gradient(135deg, #1a3d2a 0%, #1e4d35 100%);
  color: #8bffb3;
}
[data-bs-theme="dark"] .device-name,
[data-theme="dark"] .device-name,
.dark .device-name {
  color: #e4e6eb;
}
[data-bs-theme="dark"] .info-item,
[data-theme="dark"] .info-item,
.dark .info-item {
  color: #b0b3b8;
}
[data-bs-theme="dark"] .info-item i,
[data-theme="dark"] .info-item i,
.dark .info-item i {
  color: #00e050;
}
[data-bs-theme="dark"] .device-card-footer,
[data-theme="dark"] .device-card-footer,
.dark .device-card-footer {
  background: linear-gradient(135deg, #2d2e30 0%, #242526 100%);
  border-color: rgba(0, 196, 68, 0.1);
}
[data-bs-theme="dark"] .devices-loading p,
[data-theme="dark"] .devices-loading p,
.dark .devices-loading p {
  color: #b0b3b8;
}
[data-bs-theme="dark"] .devices-empty h5,
[data-theme="dark"] .devices-empty h5,
.dark .devices-empty h5 {
  color: #e4e6eb;
}
[data-bs-theme="dark"] .devices-empty p,
[data-theme="dark"] .devices-empty p,
.dark .devices-empty p {
  color: #b0b3b8;
}
[data-bs-theme="dark"] .empty-icon,
[data-theme="dark"] .empty-icon,
.dark .empty-icon {
  background: linear-gradient(135deg, #1a3d2a 0%, #1e4d35 100%);
  border-color: rgba(0, 196, 68, 0.4);
}
[data-bs-theme="dark"] .empty-icon i,
[data-theme="dark"] .empty-icon i,
.dark .empty-icon i {
  color: rgba(0, 196, 68, 0.6);
}

[data-bs-theme="dark"] .breadcrumb-item.text-muted,
[data-theme="dark"] .breadcrumb-item.text-muted,
.dark .breadcrumb-item.text-muted {
  color: #b0b3b8 !important;
}
[data-bs-theme="dark"] .breadcrumb-item a,
[data-theme="dark"] .breadcrumb-item a,
.dark .breadcrumb-item a {
  color: #00e050 !important;
}

[data-bs-theme="dark"] ::-webkit-scrollbar-track,
[data-theme="dark"] ::-webkit-scrollbar-track,
.dark ::-webkit-scrollbar-track {
  background: #242526;
}

/* ========== Phase 2: Settings Options ========== */
.settings-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.options-group {
  background: #f8f9fa;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(0, 196, 68, 0.1);
}

.option-item {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.option-item:last-child {
  border-bottom: none;
}

.option-item:hover {
  background: rgba(0, 196, 68, 0.05);
}

.option-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(0, 196, 68, 0.1) 0%, rgba(0, 196, 68, 0.05) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 12px;
  color: #00c444;
  font-size: 16px;
}

.option-icon.text-danger {
  background: linear-gradient(135deg, rgba(220, 53, 69, 0.1) 0%, rgba(220, 53, 69, 0.05) 100%);
  color: #dc3545;
}

.option-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.option-title {
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.option-value {
  font-size: 12px;
  color: #6c757d;
}

.option-arrow {
  color: #adb5bd;
  font-size: 12px;
}

/* ========== Settings Modals ========== */
.modal-content {
  border-radius: 24px !important;
  overflow: hidden;
  border: none;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.15);
}

.modal-header {
  background: linear-gradient(135deg, #00c444 0%, #00a43c 100%);
  padding: 20px 24px;
  border: none;
}

.modal-title {
  color: #fff !important;
  font-weight: 700;
  font-size: 1.2rem;
}

.modal-header .btn-light {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: #fff;
  border-radius: 12px;
  padding: 8px 16px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.modal-header .btn-light:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
}

.modal-body {
  background: linear-gradient(145deg, #f8fffe 0%, #ffffff 100%);
  padding: 30px;
}

/* Dark mode for modals */
[data-bs-theme="dark"] .modal-content,
[data-theme="dark"] .modal-content,
.dark .modal-content {
  background: #242526;
  border-color: rgba(0, 196, 68, 0.15);
}

[data-bs-theme="dark"] .modal-body,
[data-theme="dark"] .modal-body,
.dark .modal-body {
  background: linear-gradient(145deg, #1a1b1d 0%, #242526 100%);
}

.language-options,
.theme-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.language-option,
.theme-option {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid #e9ecef;
}

.language-option:hover,
.theme-option:hover {
  background: rgba(0, 196, 68, 0.05);
  border-color: rgba(0, 196, 68, 0.2);
}

.language-option.active,
.theme-option.active {
  background: rgba(0, 196, 68, 0.1);
  border-color: #00c444;
}

.language-option .lang-name {
  flex: 1;
  font-weight: 500;
}

.theme-option i:first-child {
  margin-left: 12px;
  color: #6c757d;
  width: 20px;
}

.theme-option span {
  flex: 1;
  font-weight: 500;
}

/* ========== Device QR Modal ========== */
.qr-container-modal {
  padding: 20px;
  background: #f8f9fa;
  border-radius: 16px;
  display: inline-block;
}

.device-qr-image {
  width: 200px;
  height: 200px;
  border-radius: 8px;
}

.qr-loading {
  width: 200px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ========== About Modal ========== */
.about-content {
  text-align: center;
  padding: 16px;
  color: #495057;
  line-height: 1.8;
}

/* ========== Dark Mode for Phase 2 ========== */
[data-bs-theme="dark"] .options-group,
[data-theme="dark"] .options-group,
.dark .options-group {
  background: #2d2e30;
  border-color: rgba(0, 196, 68, 0.15);
}

[data-bs-theme="dark"] .option-item,
[data-theme="dark"] .option-item,
.dark .option-item {
  border-color: rgba(255, 255, 255, 0.05);
}

[data-bs-theme="dark"] .option-item:hover,
[data-theme="dark"] .option-item:hover,
.dark .option-item:hover {
  background: rgba(0, 196, 68, 0.1);
}

[data-bs-theme="dark"] .option-title,
[data-theme="dark"] .option-title,
.dark .option-title {
  color: #e4e6eb;
}

[data-bs-theme="dark"] .option-value,
[data-theme="dark"] .option-value,
.dark .option-value {
  color: #b0b3b8;
}

[data-bs-theme="dark"] .modal-content,
[data-theme="dark"] .modal-content,
.dark .modal-content {
  background: #242526;
  border-color: rgba(0, 196, 68, 0.15);
}

[data-bs-theme="dark"] .modal-title,
[data-theme="dark"] .modal-title,
.dark .modal-title {
  color: #e4e6eb;
}

[data-bs-theme="dark"] .language-option,
[data-bs-theme="dark"] .theme-option,
[data-theme="dark"] .language-option,
[data-theme="dark"] .theme-option,
.dark .language-option,
.dark .theme-option {
  border-color: #3a3b3c;
  color: #e4e6eb;
}

[data-bs-theme="dark"] .language-option:hover,
[data-bs-theme="dark"] .theme-option:hover,
[data-theme="dark"] .language-option:hover,
[data-theme="dark"] .theme-option:hover,
.dark .language-option:hover,
.dark .theme-option:hover {
  background: rgba(0, 196, 68, 0.1);
  border-color: rgba(0, 196, 68, 0.3);
}

[data-bs-theme="dark"] .qr-container-modal,
[data-theme="dark"] .qr-container-modal,
.dark .qr-container-modal {
  background: #3a3b3c;
}

[data-bs-theme="dark"] .about-content,
[data-theme="dark"] .about-content,
.dark .about-content {
  color: #b0b3b8;
}

/* ========== Phase 3: FAQ Styles ========== */
.faq-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.faq-item {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  overflow: hidden;
}

.faq-question {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  cursor: pointer;
  background: #f8f9fa;
  font-weight: 500;
  transition: all 0.2s ease;
}

.faq-question:hover {
  background: rgba(0, 196, 68, 0.05);
}

.faq-question.active {
  background: rgba(0, 196, 68, 0.1);
  color: #00c444;
}

.faq-question i {
  color: #6c757d;
  font-size: 12px;
}

.faq-answer {
  padding: 16px;
  background: #fff;
  border-top: 1px solid #e9ecef;
  line-height: 1.8;
  color: #495057;
}

.faq-answer :deep(p) {
  margin-bottom: 8px;
}

.faq-answer :deep(p:last-child) {
  margin-bottom: 0;
}

/* ========== Phase 3: Contact Styles ========== */
.contact-cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.contact-card {
  display: flex;
  align-items: center;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  cursor: pointer;
  transition: all 0.2s ease;
}

.contact-card:hover {
  background: rgba(0, 196, 68, 0.05);
  border-color: rgba(0, 196, 68, 0.2);
}

.contact-card-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(0, 196, 68, 0.15) 0%, rgba(0, 196, 68, 0.05) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 14px;
  color: #00c444;
  font-size: 18px;
}

.contact-card-content {
  flex: 1;
}

.contact-card-content h6 {
  margin: 0 0 4px 0;
  font-size: 12px;
  color: #6c757d;
  font-weight: 500;
}

.contact-card-content p {
  margin: 0;
  font-weight: 500;
  color: #333;
}

.contact-card-action {
  color: #adb5bd;
  font-size: 14px;
}

.contact-form-section {
  padding-top: 16px;
  border-top: 1px solid #e9ecef;
}

/* Slide fade transition */
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s ease-in;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
  max-height: 0;
  padding: 0 16px;
}

.slide-fade-enter-to,
.slide-fade-leave-from {
  opacity: 1;
  max-height: 500px;
}

/* ========== Dark Mode for Phase 3 ========== */
[data-bs-theme="dark"] .faq-item,
[data-theme="dark"] .faq-item,
.dark .faq-item {
  border-color: #3a3b3c;
}

[data-bs-theme="dark"] .faq-question,
[data-theme="dark"] .faq-question,
.dark .faq-question {
  background: #2d2e30;
  color: #e4e6eb;
}

[data-bs-theme="dark"] .faq-question:hover,
[data-theme="dark"] .faq-question:hover,
.dark .faq-question:hover {
  background: rgba(0, 196, 68, 0.1);
}

[data-bs-theme="dark"] .faq-answer,
[data-theme="dark"] .faq-answer,
.dark .faq-answer {
  background: #242526;
  border-color: #3a3b3c;
  color: #b0b3b8;
}

[data-bs-theme="dark"] .contact-card,
[data-theme="dark"] .contact-card,
.dark .contact-card {
  background: #2d2e30;
  border-color: #3a3b3c;
}

[data-bs-theme="dark"] .contact-card:hover,
[data-theme="dark"] .contact-card:hover,
.dark .contact-card:hover {
  background: rgba(0, 196, 68, 0.1);
  border-color: rgba(0, 196, 68, 0.3);
}

[data-bs-theme="dark"] .contact-card-content p,
[data-theme="dark"] .contact-card-content p,
.dark .contact-card-content p {
  color: #e4e6eb;
}

[data-bs-theme="dark"] .contact-form-section,
[data-theme="dark"] .contact-form-section,
.dark .contact-form-section {
  border-color: #3a3b3c;
}

/* ========== Phase 4: Chat Settings Styles ========== */
.chat-settings-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.setting-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #e9ecef;
}

.setting-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  margin-left: 16px;
}

.setting-title {
  font-weight: 500;
  color: #333;
}

.setting-desc {
  font-size: 12px;
  color: #6c757d;
}

/* Custom Switch */
.switch-container {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 28px;
}

.switch-input {
  opacity: 0;
  width: 0;
  height: 0;
}

.switch-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.3s;
  border-radius: 28px;
}

.switch-slider:before {
  position: absolute;
  content: "";
  height: 22px;
  width: 22px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.switch-input:checked + .switch-slider {
  background-color: #00c444;
}

.switch-input:checked + .switch-slider:before {
  transform: translateX(22px);
}

/* Chat QR Styles */
.chat-qr-content {
  text-align: center;
  padding: 8px;
}

.chat-user-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
}

.chat-user-avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid rgba(0, 196, 68, 0.2);
}

.chat-user-name {
  margin: 0;
  font-weight: 600;
  color: #333;
}

.shareable-id-box {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 12px 16px;
  display: inline-block;
  min-width: 100%;
}

.shareable-id-label {
  display: block;
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 6px;
}

.shareable-id-value {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-weight: 600;
  font-size: 16px;
  color: #333;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #e9ecef;
  transition: all 0.2s ease;
}

.shareable-id-value:hover {
  border-color: #00c444;
  background: rgba(0, 196, 68, 0.05);
}

.shareable-id-value code {
  font-size: 15px;
  color: #00a43c;
  word-break: break-all;
}

.shareable-id-value i {
  color: #6c757d;
  font-size: 16px;
  transition: color 0.2s ease;
}

.shareable-id-value:hover i {
  color: #00c444;
}

/* ========== Dark Mode for Phase 4 ========== */
[data-bs-theme="dark"] .setting-item,
[data-theme="dark"] .setting-item,
.dark .setting-item {
  background: #2d2e30;
  border-color: #3a3b3c;
}

[data-bs-theme="dark"] .setting-title,
[data-theme="dark"] .setting-title,
.dark .setting-title {
  color: #e4e6eb;
}

[data-bs-theme="dark"] .setting-desc,
[data-theme="dark"] .setting-desc,
.dark .setting-desc {
  color: #b0b3b8;
}

[data-bs-theme="dark"] .shareable-id-box,
[data-theme="dark"] .shareable-id-box,
.dark .shareable-id-box {
  background: #3a3b3c;
}

[data-bs-theme="dark"] .shareable-id-value,
[data-theme="dark"] .shareable-id-value,
.dark .shareable-id-value {
  background: #242526;
  border-color: #3a3b3c;
  color: #e4e6eb;
}

[data-bs-theme="dark"] .shareable-id-value:hover,
[data-theme="dark"] .shareable-id-value:hover,
.dark .shareable-id-value:hover {
  border-color: #00c444;
  background: rgba(0, 196, 68, 0.1);
}

[data-bs-theme="dark"] .chat-user-name,
[data-theme="dark"] .chat-user-name,
.dark .chat-user-name {
  color: #e4e6eb;
}
</style>
